import React, { useState, useEffect } from "react";
import { Menu, MenuItem, Checkbox, FormControlLabel, Button, TextField, Tooltip } from "@material-ui/core";
import { FaChevronDown as ArrowDropDownIcon } from "react-icons/fa";

const DropdownWithCheckboxes = ({ options, title, icon, onApply, onInitialLoad, defaultSelectAll = false, compact = false }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState(
        defaultSelectAll ? options.map(opt => opt.value) : []
    );
    const [tempSelected, setTempSelected] = useState([...selectedOptions]);
    const [searchText, setSearchText] = useState(""); // Search state

    const isAllSelected = tempSelected.length === options.length;

    useEffect(() => {
        if (onInitialLoad) {
            onInitialLoad(selectedOptions);
        }
    }, []);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSearchText(""); // Reset search on close
    };

    const handleSelectAll = () => {
        setTempSelected(isAllSelected ? [] : options.map(opt => opt.value));
    };

    const handleCheckboxChange = (value) => {
        setTempSelected(tempSelected.includes(value) 
            ? tempSelected.filter(item => item !== value) 
            : [...tempSelected, value]
        );
    };

    const handleApply = () => {
        setSelectedOptions(tempSelected);
        onApply(tempSelected);
        handleClose();
    };

    const handleCancel = () => {
        setTempSelected(selectedOptions);
        handleClose();
    };

    // Filter items based on search input
    const filteredOptions = options.filter(option => 
        option.label.toLowerCase().includes(searchText.toLowerCase())
    );

    return (
        <div>
            <Tooltip title={title} arrow placement="bottom">
                <Button
                    variant="contained"
                    onClick={handleClick}
                    endIcon={<ArrowDropDownIcon style={{ fontSize: "14px" }} />}
                    style={{
                        backgroundColor: selectedOptions.length > 0 ? "rgb(34, 41, 180)" : "#013f82",
                        color: "rgb(169, 227, 251)",
                        borderRadius: "20px",
                        border: selectedOptions.length > 0 ? "1px solid rgb(41, 215, 224)" : "2px solid transparent",
                        boxShadow: selectedOptions.length > 0 ? "0px 0px 8px rgb(168, 224, 248)" : "none",
                        padding: compact ? "4px 8px" : "4px 16px",
                        textTransform: "none",
                        fontWeight: "normal",
                        minHeight: "30px",
                        minWidth: compact ? "60px" : "120px",
                        fontSize: "14px"
                    }}
                >
                    {icon ? (
                        <img 
                            src={icon} 
                            alt={title || "Icon"} 
                            style={{ 
                                width: "28px", 
                                height: "28px",
                                filter: "brightness(0) saturate(100%) invert(88%) sepia(15%) saturate(1234%) hue-rotate(169deg) brightness(101%) contrast(101%)"
                            }} 
                        />
                    ) : (
                        title
                    )}
                </Button>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                keepMounted
                disableAutoFocusItem // 💡 Prevents auto-focus on menu items
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                    style: {
                        backgroundColor: "#2b2f3a",
                        color: "#fff",
                        borderRadius: "10px",
                        minWidth: "180px",
                        maxHeight: "300px",
                        overflowY: "auto",
                        padding: "5px",
                        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)"
                    }
                }}
            >
                {/* Search Box */}
                <div style={{ padding: "6px 10px", backgroundColor: "#2b2f3a" }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        placeholder="Search..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        autoFocus // 💡 Ensures typing stays in the box
                        onKeyDown={(e) => e.stopPropagation()} // 💡 Prevents default menu item selection
                        InputProps={{
                            style: {
                                backgroundColor: "#2b2f3a",
                                color: "#fff",
                                fontSize: "12px",
                                borderRadius: "5px",
                                border: "1px solid #3c82f6"
                            }
                        }}
                        InputLabelProps={{
                            style: { color: "#ffffff80", fontSize: "12px" }
                        }}
                    />
                </div>

                {/* Select All Option (Only Show If Items Exist) */}
                {filteredOptions.length > 0 && (
                    <MenuItem 
                        style={{ fontSize: "12px", padding: "2px 8px", minHeight: "20px" }}
                        onClick={(e) => e.stopPropagation()} // 💡 Prevents menu from closing when clicking "Select All"
                    >
                        <FormControlLabel
                            control={
                                <Checkbox 
                                    size="small" 
                                    checked={isAllSelected} 
                                    onChange={handleSelectAll} 
                                    style={{ color: isAllSelected ? "#3c82f6" : "#ffffff80" }} 
                                />
                            }
                            label={<span style={{ fontSize: "12px" }}>Select All</span>}
                        />
                    </MenuItem>
                )}

                {/* Filtered Options */}
                {filteredOptions.length > 0 ? (
                    filteredOptions.map(option => (
                        <MenuItem 
                            key={option.value} 
                            style={{ fontSize: "12px", padding: "2px 8px" }}
                            onClick={(e) => e.stopPropagation()} // 💡 Prevents menu from closing when clicking an item
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        size="small" 
                                        checked={tempSelected.includes(option.value)} 
                                        onChange={() => handleCheckboxChange(option.value)} 
                                        style={{ color: tempSelected.includes(option.value) ? "#3c82f6" : "#ffffff80" }} 
                                    />
                                }
                                label={
                                    <span style={{ fontSize: "12px", color: "#ffffff" }}>
                                        {option.label}
                                    </span>
                                }
                            />
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled style={{ fontSize: "12px", color: "#ffffff80", padding: "6px 8px" }}>
                        No results found
                    </MenuItem>
                )}

                {/* Apply & Cancel Buttons */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px",
                    borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                    backgroundColor: "#2b2f3a", // Slightly darker shade for footer
                    borderRadius: "0px 0px 8px 8px",
                    position: "sticky",
                    bottom: 0
                }}>
                    <Button 
                        onClick={handleCancel} 
                        style={{ 
                            backgroundColor: "transparent", 
                            color: "rgba(252, 252, 252, 0.5)", 
                            fontSize: "12px", 
                            padding: "4px 10px", 
                            textTransform: "none"
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleApply} 
                        style={{ 
                            backgroundColor: "#3c82f6", // Elegant golden yellow
                            color: "#fff", 
                            fontSize: "12px", 
                            padding: "4px 10px", 
                            textTransform: "none",
                            borderRadius: "5px",
                            fontWeight: "bold"
                        }}
                    >
                        Apply
                    </Button>
                </div>
            </Menu>
        </div>
    );
};

export default DropdownWithCheckboxes;